import {noop} from '@benchling/js-utils';
import React from 'react';
import isEqual from 'react-fast-compare';
import {connect} from 'react-redux';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SchemaTag, Tag} from 'folder-items/FileConstants';
import {ConnectionStatus} from 'instruments/connections/ConnectionConstants';
import TagValueEditor from 'metadata/TagValueEditor';
import {isFieldTypeSupportedForManualEntry} from 'notebook/data-tables/DataTableConfigEditUtil';
import {EditorPropertiesContext, NoteContext} from 'notebook/notes/core-ui/NoteContext';
import addSamplesFromTableToTag from 'notebook/sample-propagation/add-samples-to-tag/addSamplesFromTableToTag';
import addSamplesFromWorklistToTag from 'notebook/sample-propagation/add-samples-to-tag/addSamplesFromWorklistToTag';
import * as addSamplesToTagUtils from 'notebook/sample-propagation/add-samples-to-tag/addSamplesToTagUtils';
import {SampleFilter} from 'notebook/sample-propagation/SampleFilter';
import {Note} from 'notebook/util/NoteUtilConstants';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import {BenchlingState} from 'redux-stores/reduceApp';
import {BenchlingDispatch} from 'redux-stores/Store';
import {GetAnalysisTemplateForAssayRun_analysisTemplate_AnalysisTemplateNode as AnalysisTemplateNode} from 'results/analysis-integration/__generated/GetAnalysisTemplateForAssayRun';
import {AssayRun, RUN_OUTPUT_TABLE_ICON} from 'results/assays/AssayRecordConstants';
import {AssayRecordField} from 'results/assays/AssayRecordRendering';
import {getTagForField} from 'results/assays/getTagForField';
import {
  connectionUpdated,
  equipmentUpdated,
  initialTagsSet,
  studiesUpdated,
  tagsReset,
  tagUpdated,
} from 'runs/assay-group/TagsReduxActions';
import isSchemaFieldArchived from 'schemas/fields/isSchemaFieldArchived';
import {FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {findSchemaTag} from 'schemas/tags/TagUtil';
import StudySearchInput, {StudySearchItem} from 'studies/StudySearchInput';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import InfoIcon from 'ui-library/icons/InfoIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {OptionData} from 'ui-library/menus/MenuConstants';
import * as DateUtil from 'util/DateUtil';
import {Optional} from 'util/Types';

import AssayDataSourceSelector, {AssayDataSource} from './AssayDataSourceSelector';
import {getVisibleRunTags} from './VisibleRunFields';

const EmptyField: React.FunctionComponent = () => (
  <div className="resultsAttachment-assayRun-emptyFieldValue" />
);

function shouldSummarizeField(field: SchemaField): boolean {
  return (
    field.isMulti &&
    (field.fieldType === FieldType.BIOENTITY_LINK || field.fieldType === FieldType.STORABLE_LINK)
  );
}

interface AssayRunFieldWrapperProps {
  assayRun?: AssayRun;
  children: React.ReactNode;
}

/**
 * This component wraps all the fields for an assay run (in either editable or read-only state)
 */
function AssayRunFieldsWrapper(props: AssayRunFieldWrapperProps): JSX.Element {
  return (
    <Row bufferRowReset={true}>
      {React.Children.toArray(props.children).map((child: JSX.Element, i) => (
        <div key={`col-${i}`}>
          {i % 3 === 0 && <Col className="clearfix" key={`clearfix-${i}`} />}
          <Col className="resultsAttachment-assayRun-fieldSection" key={i} sm={4}>
            {child}
          </Col>
        </div>
      ))}
    </Row>
  );
}

interface AssayRunFieldProps {
  isRequired?: boolean;
  isArchived?: boolean;
  key: string;
  title: string;
  children: React.ReactNode;
  dataTestAttrs?: DataTestAttrs;
  tooltipText?: string | null;
}

/**
 * Wraps a single run field (in either editable or read-only state)
 */
export function AssayRunField(props: AssayRunFieldProps): JSX.Element {
  return (
    <>
      <FieldLabel
        className="resultsAttachment-assayRun-fieldHeader u-tiny-spacing-bottom"
        isRequired={props.isRequired}
        label={props.title + (props.isArchived ? ' (Archived)' : '')}
        grayOutForeground={props.isArchived}
      />
      {props.tooltipText && (
        <span className="u-mini-spacing-left">
          <Popover
            renderContent={() => <span>{props.tooltipText}</span>}
            placement={FloatingPlacement.TOP}
            interaction={FloatingInteraction.HOVER}
          >
            <InfoIcon />
          </Popover>
        </span>
      )}
      <div className="resultsAttachment-assayRun-fieldValue" {...getTestProps(props.dataTestAttrs)}>
        {props.children}
      </div>
    </>
  );
}

interface ReadOnlyAssayRunFieldsProps {
  assayRun?: AssayRun;
  analysisTemplate?: AnalysisTemplateNode;
  liveFields: Array<SchemaField>;
  versionedFields: Array<SchemaField> | null;
  versionedTags: Array<Tag> | null;
  versionedEquipmentId: string | null;
  versionedConnectionId: string | null;
  connectionSchemaId: string | null;
  connectionStatus?: ConnectionStatus | null;
  shouldRecordEquipment?: boolean;

  // Is the Notebook entry in a print-preview or viewer mode?
  viewer?: boolean;
}

/**
 * Component for displaying assay run fields.
 */
export function ReadOnlyAssayRunFields(props: ReadOnlyAssayRunFieldsProps): JSX.Element {
  const fields = props.versionedFields || props.liveFields;

  const creator = props.assayRun?.creator;
  const connectionId = props.versionedConnectionId ?? props.assayRun?.connection?.id ?? null;
  const equipmentId = props.versionedEquipmentId ?? props.assayRun?.equipment?.id ?? null;
  const shouldRecordEquipment = props.shouldRecordEquipment ?? props.assayRun?.shouldRecordEquipment ?? false;

  return (
    <>
      <AssayRunFieldsWrapper assayRun={props.assayRun}>
        <AssayRunField
          title="Creator"
          key="creator"
          dataTestAttrs={{
            component: 'AssayRunFieldsWrapper',
            element: 'creator',
          }}
        >
          {creator ? getPartyDisplayString(creator) : <EmptyField />}
        </AssayRunField>
        <AssayRunField
          title="Created At"
          key="created-at"
          dataTestAttrs={{
            component: 'AssayRunFieldsWrapper',
            element: 'createdAt',
          }}
        >
          {props.assayRun == null ? null : DateUtil.readableTime(props.assayRun.createdAt)}
        </AssayRunField>
        {(shouldRecordEquipment || props.connectionSchemaId) && (
          <AssayDataSource
            shouldRecordEquipment={shouldRecordEquipment}
            connectionId={connectionId}
            selectedEquipmentId={equipmentId}
            connectionSchemaId={props.connectionSchemaId}
            connectionStatus={props.connectionStatus}
            viewer={props.viewer}
          />
        )}
        {BenchlingConfig.STUDIES_ENABLE && props.assayRun?.studyIds && props.assayRun.studyIds.length > 0 && (
          <AssayRunField title="Studies" key="studies">
            <StudySearchInput<'id'>
              onChange={() => {}}
              shouldAllowMultiple={true}
              selectedItems={props.assayRun.studyIds.map((studyId) => ({id: studyId}))}
              isReadOnly={true}
              hasNonInteractiveChips={false}
            />
          </AssayRunField>
        )}
        {fields.map((field, i) => {
          const versionedTag = props.versionedTags ? props.versionedTags[i] : null;
          const tag = versionedTag || (props.assayRun ? getTagForField(props.assayRun, field.id) : null);

          return (
            <AssayRunField
              key={field.name}
              title={field.displayName}
              isArchived={isSchemaFieldArchived(field)}
              isRequired={field.isRequired}
              dataTestAttrs={{
                component: 'AssayRunFieldsWrapper',
                element: field.name,
              }}
              tooltipText={field.tooltipText}
            >
              {tag == null ? (
                <EmptyField />
              ) : (
                <AssayRecordField
                  tag={tag}
                  // Some fields are summarized, rather than rendered with one chip per value
                  summarize={shouldSummarizeField(field)}
                />
              )}
            </AssayRunField>
          );
        })}
      </AssayRunFieldsWrapper>
    </>
  );
}

interface EditableRunFieldProps {
  isDisabled?: boolean;
  note: Note | null;
  entryId: string | null;
  cid: string;
  assayRun?: AssayRun;
  field: SchemaField;
  tag: SchemaTag | null;
  tagIndex: number;
  onUpdateTag: (cid: string, tagIndex: number, tag: Tag) => void;
}

interface TagValueEditorWithAddSamplesProps extends EditableRunFieldProps {
  tag: SchemaTag;
}

// Exported for testing only
export const _runAddSamplesFromTable = async ({
  field,
  entryId,
  tag,
  onUpdateTag,
  note,
  cid,
  tagIndex,
  modalTitleForTables,
}: TagValueEditorWithAddSamplesProps & {modalTitleForTables: string}): Promise<void> => {
  const narrowFilter = addSamplesToTagUtils.getSampleSourceFilterForField(tag, field.fieldType);
  let destinationSampleType: SampleFilter | null = null;
  if (narrowFilter.length === 1) {
    destinationSampleType = Array.from(narrowFilter)[0];
  }
  assert(note != null && entryId != null, 'Called addSamplesFromTable with null `note` or `entryId`');
  const newTag = await addSamplesFromTableToTag(note, tag, field.fieldType, narrowFilter, {
    entryId,
    title: modalTitleForTables,
    destinationSampleType,
  });
  if (newTag != null) {
    onUpdateTag(cid, tagIndex, newTag);
  }
};

// Exported for testing only
export const _runAddSamplesFromWorklist = async ({
  field,
  tag,
  onUpdateTag,
  note,
  cid,
  tagIndex,
}: TagValueEditorWithAddSamplesProps): Promise<void> => {
  const narrowFilter = addSamplesToTagUtils.getSampleSourceFilterForField(tag, field.fieldType);
  assert(note != null, 'Called addSamplesFromWorklist with null `note`');
  const newTag = await addSamplesFromWorklistToTag(note, tag, field.fieldType, narrowFilter);
  if (newTag != null) {
    onUpdateTag(cid, tagIndex, newTag);
  }
};

// Renders a TagValueEditor, but provides "Add Samples from Worklist" and "from Structured Table" tools
export function TagValueEditorWithAddSamples(props: TagValueEditorWithAddSamplesProps): JSX.Element {
  const {field, tag} = props;
  const modalTitleForTables = addSamplesToTagUtils.addSamplesTitleForField(
    tag,
    field.fieldType,
    'structured table'
  );

  const runAddSamplesFromTable = async (): Promise<void> => {
    await _runAddSamplesFromTable({...props, modalTitleForTables});
  };

  const runAddSamplesFromWorklist = async (): Promise<void> => {
    await _runAddSamplesFromWorklist(props);
  };

  const noResultsOptions: Array<{option: OptionData; onClick: () => void}> = [
    {
      option: {
        name: modalTitleForTables,
        icon: RUN_OUTPUT_TABLE_ICON,
        className: 'u-pointer',
        dataTestAttrs: {
          component: 'TagValueEditorWithAddSamples',
          element: 'noResultsOptions',
          key: 'runAddSamplesFromTable',
        },
      },
      onClick: runAddSamplesFromTable,
    },
    {
      option: {
        name: addSamplesToTagUtils.addSamplesTitleForField(tag, field.fieldType, 'worklist'),
        icon: ModelTypeConstants.WORKLIST,
        className: 'u-pointer',
      },
      onClick: runAddSamplesFromWorklist,
    },
  ];

  return (
    <TagValueEditor
      itemLinkAllowed={true}
      onChange={(newValue: Tag) => {
        props.onUpdateTag(props.cid, props.tagIndex, newValue);
      }}
      schemaFieldSelectorId={field.schemaFieldSelectorId}
      tag={tag}
      // Some fields are summarized, rather than rendered with one chip per value
      summarize={shouldSummarizeField(field)}
      noResultsOptions={noResultsOptions}
      onKeyDown={noop}
      isDisabled={props.isDisabled}
    />
  );
}

function EditableRunField(props: EditableRunFieldProps): JSX.Element | null {
  const {field, tag} = props;
  const includeAddSamplesMenuItemInThisTag =
    props.note != null &&
    props.entryId != null &&
    tag != null &&
    addSamplesToTagUtils.canAddSamplesFromModalToTag(tag, field.fieldType);

  if (tag == null) {
    return null;
  } else if (isSchemaFieldArchived(field) || !isFieldTypeSupportedForManualEntry(field.fieldType)) {
    return <AssayRecordField tag={tag} />;
  } else if (includeAddSamplesMenuItemInThisTag) {
    return <TagValueEditorWithAddSamples {...props} tag={tag} />;
  } else {
    return (
      <TagValueEditor
        isDisabled={(props.note == null && props.entryId == null) || props.isDisabled}
        itemLinkAllowed={true}
        onChange={(newValue: Tag) => {
          props.onUpdateTag(props.cid, props.tagIndex, newValue);
        }}
        schemaFieldSelectorId={field.schemaFieldSelectorId}
        tag={tag}
        // Some fields are summarized, rather than rendered with one chip per value
        summarize={shouldSummarizeField(field)}
        onKeyDown={noop}
      />
    );
  }
}

interface EditableOwnProps {
  displayAnalysisTemplateIntegrationUI?: boolean;
  isDisabled?: boolean;
  assayRun?: AssayRun;
  analysisTemplate?: AnalysisTemplateNode;
  liveFields: Array<SchemaField>;
  versionedFields: Array<SchemaField> | null;
  versionedTags: Array<Tag> | null;
  cid: string;
  versionedConnectionId: string | null;
  versionedEquipmentId: string | null;
  connectionSchemaId: string | null;
  hasAutomationFiles: boolean;
  shouldRecordEquipment?: boolean;

  // Is the Notebook entry in a print-preview or viewer mode?
  viewer?: boolean;
}

interface EditableConnectedProps {
  draftTags: Optional<Array<Tag>>;
  draftConnectionId: Optional<string>;
  draftEquipmentId: Optional<string>;
  draftStudyIds: Optional<Array<string>>;
}

interface EditableDispatchProps {
  onSetInitialTags: (
    cid: string,
    tags: Array<Tag>,
    connectionId: string | null,
    equipmentId: string | null,
    studyIds: Array<string>
  ) => void;
  onUpdateTag: (cid: string, tagIndex: number, tag: Tag) => void;
  onUpdateConnection: (cid: string, connectionId: string | null) => void;
  onUpdateEquipment: (cid: string, equipmentId: string | null, connectionId?: string | null) => void;
  onUpdateStudies: (cid: string, studyIds: Array<string>) => void;
  onResetTags: (cid: string) => void;
}

type EditableAssayRunFieldsProps = EditableOwnProps & EditableConnectedProps & EditableDispatchProps;

/**
 * Component for editing assay run fields.
 */
export class EditableAssayRunFields extends React.Component<EditableAssayRunFieldsProps> {
  override componentDidMount(): void {
    if (!this.props.draftTags) {
      // Set initial tags if not already available
      this.props.onSetInitialTags(
        this.props.cid,
        this.props.versionedTags || getVisibleRunTags(this.props.liveFields, this.props.assayRun),
        this.props.versionedConnectionId ?? this.props.assayRun?.connection?.id ?? null,
        this.props.versionedEquipmentId ?? this.props.assayRun?.equipment?.id ?? null,
        this.props.assayRun?.studyIds || []
      );
    }
  }

  override componentWillUnmount(): void {
    if (this.props.assayRun) {
      // Only reset tags if edit existing assay run because:
      //   - If create new assay run, the user expects the state to persist across mounts. That way, if
      //     create fails, then they can continue where the left off - edit the previous tags and retry.
      //   - If edit existing assay run, the user expects the state to reset to the assay run's tags. That
      //     way, if save fails, then they can restart - make a new edit to the tags and retry.
      this.props.onResetTags(this.props.cid);
    }
  }

  _renderFields(note: Note | null, entryId: string | null): JSX.Element {
    const {props} = this;
    const fields = props.versionedFields || props.liveFields;

    const tags = props.draftTags || props.versionedTags || [];
    const connectionId = props.draftConnectionId ?? props.versionedConnectionId;
    const equipmentId = props.draftEquipmentId;
    const shouldRecordEquipment = this.props.shouldRecordEquipment ?? props.assayRun?.shouldRecordEquipment;
    const studyIds = props.draftStudyIds ?? props.assayRun?.studyIds;
    const studySearchItems: Array<StudySearchItem> = studyIds
      ? studyIds.map((studyId) => ({id: studyId, name: '', humanId: ''}))
      : [];
    const creator = props.assayRun?.creator;
    const areStudiesEditable = BenchlingConfig.STUDIES_ENABLE && !props.hasAutomationFiles;

    return (
      <>
        <AssayRunFieldsWrapper assayRun={props.assayRun}>
          {!props.displayAnalysisTemplateIntegrationUI && (
            <AssayRunField title="Creator" key="creator">
              {creator ? getPartyDisplayString(creator) : <EmptyField />}
            </AssayRunField>
          )}
          {!props.displayAnalysisTemplateIntegrationUI && (
            <AssayRunField title="Created At" key="created-at">
              {props.assayRun == null ? null : DateUtil.readableTime(props.assayRun.createdAt)}
            </AssayRunField>
          )}
          {(shouldRecordEquipment || props.connectionSchemaId) && (
            <AssayDataSourceSelector
              shouldRecordEquipment={shouldRecordEquipment ?? false}
              selectedEquipmentId={equipmentId || null}
              onUpdateEquipment={props.onUpdateEquipment}
              connectionSchemaId={props.connectionSchemaId}
              connectionId={connectionId}
              onUpdateConnection={props.onUpdateConnection}
              cid={props.cid}
              hasAutomationFiles={props.hasAutomationFiles}
              viewer={props.viewer}
            />
          )}
          {BenchlingConfig.STUDIES_ENABLE && (
            <AssayRunField title="Studies" key="studies">
              <StudySearchInput
                onChange={(studySearchItems: Array<StudySearchItem>) => {
                  const onChangeStudyIds = studySearchItems.map((studySearchItem) => studySearchItem.id);
                  this.props.onUpdateStudies(props.cid, onChangeStudyIds);
                }}
                shouldAllowMultiple={true}
                selectedItems={studySearchItems}
                disabled={props.isDisabled}
                isReadOnly={!areStudiesEditable}
                hasNonInteractiveChips={true}
              />
            </AssayRunField>
          )}
          {fields.map((field) => {
            const tag = findSchemaTag(tags, field.id);
            return (
              <AssayRunField
                key={field.name}
                title={field.displayName}
                isArchived={isSchemaFieldArchived(field)}
                isRequired={field.isRequired}
                tooltipText={field.tooltipText}
              >
                <EditableRunField
                  isDisabled={props.isDisabled}
                  note={note}
                  entryId={entryId}
                  cid={props.cid}
                  assayRun={props.assayRun}
                  field={field}
                  tag={tag || null}
                  tagIndex={tags.findIndex((t) => isEqual(t, tag))}
                  onUpdateTag={props.onUpdateTag}
                />
              </AssayRunField>
            );
          })}
        </AssayRunFieldsWrapper>
      </>
    );
  }

  override render(): JSX.Element {
    return (
      <EditorPropertiesContext.Consumer>
        {({fileId}) => (
          <NoteContext.Consumer>
            {({note}) => this._renderFields(note ?? null, fileId ?? null)}
          </NoteContext.Consumer>
        )}
      </EditorPropertiesContext.Consumer>
    );
  }
}

function mapStateToProps(state: BenchlingState, ownProps: EditableOwnProps): EditableConnectedProps {
  const draftTags = state.ui.schemaTags.tagsByCid[ownProps.cid];
  const draftConnectionId = state.ui.schemaTags.connectionIdByCid[ownProps.cid];
  const draftEquipmentId = state.ui.schemaTags.equipmentIdByCid[ownProps.cid];
  const draftStudyIds = state.ui.schemaTags.studyIdsByCid[ownProps.cid];
  return {draftTags, draftConnectionId, draftEquipmentId, draftStudyIds};
}

function mapDispatchToProps(dispatch: BenchlingDispatch): EditableDispatchProps {
  return {
    onSetInitialTags: (
      cid: string,
      tags: Array<Tag>,
      connectionId: string | null,
      equipmentId: string | null,
      studyIds: Array<string>
    ) => {
      dispatch(initialTagsSet(cid, tags, connectionId, equipmentId, studyIds));
    },
    onUpdateTag: (cid: string, tagIndex: number, tag: Tag) => {
      dispatch(tagUpdated(cid, tagIndex, tag));
    },
    onUpdateConnection: (cid: string, connectionId: string | null) => {
      dispatch(connectionUpdated(cid, connectionId));
    },
    onUpdateEquipment: (cid: string, equipmentId: string | null, connectionId?: string | null) => {
      dispatch(equipmentUpdated(cid, equipmentId, connectionId));
    },
    onUpdateStudies: (cid: string, studyIds: Array<string>) => {
      dispatch(studiesUpdated(cid, studyIds));
    },
    onResetTags: (cid: string) => {
      dispatch(tagsReset(cid));
    },
  };
}

const ConnectedEditableAssayRunFields = connect(mapStateToProps, mapDispatchToProps)(EditableAssayRunFields);

interface AssayRunFieldsProps {
  assayRun?: AssayRun;
  analysisTemplate?: AnalysisTemplateNode;
  displayAnalysisTemplateIntegrationUI?: boolean;
  liveFields: Array<SchemaField>;
  versionedFields: Array<SchemaField> | null;
  versionedTags: Array<Tag> | null;
  cid: string;
  isEditable?: boolean;
  versionedEquipmentId: string | null;
  versionedConnectionId: string | null;
  connectionSchemaId: string | null;
  connectionStatus?: ConnectionStatus | null;
  hasAutomationFiles: boolean;
  shouldRecordEquipment?: boolean;

  // Is the Notebook in a print-preview or export mode?
  viewer?: boolean;
}

/**
 * Component for displaying and editing assay run fields.
 * If versioned fields and tags exist, we render from them, otherwise we render
 * the live fields and tags of the run.
 * See the definition of the NoteLinkedAssayData TS type for more detail on versioning.
 */
function AssayRunFields(props: AssayRunFieldsProps): JSX.Element {
  // Automation pipeline UI differences:
  // - If isEditable is false, then rather than displaying the field values as plain text, we display the
  //   field components with isDisabled=true (TODO: Always render the field components instead of plain text.
  //   Then we can just delete the ReadOnlyAssayRunFields component and its children.)
  // - We hide the "Creator" and "Created at" fields, since these are never configurable by the user

  return props.displayAnalysisTemplateIntegrationUI || props.isEditable ? (
    <ConnectedEditableAssayRunFields
      isDisabled={!props.isEditable}
      displayAnalysisTemplateIntegrationUI={props.displayAnalysisTemplateIntegrationUI}
      assayRun={props.assayRun}
      analysisTemplate={props.analysisTemplate}
      liveFields={props.liveFields}
      versionedFields={props.versionedFields}
      versionedTags={props.versionedTags}
      cid={props.cid}
      versionedEquipmentId={props.versionedEquipmentId}
      versionedConnectionId={props.versionedConnectionId}
      connectionSchemaId={props.connectionSchemaId}
      hasAutomationFiles={props.hasAutomationFiles}
      shouldRecordEquipment={props.shouldRecordEquipment}
      viewer={props.viewer}
    />
  ) : (
    <ReadOnlyAssayRunFields
      assayRun={props.assayRun}
      analysisTemplate={props.analysisTemplate}
      liveFields={props.liveFields}
      versionedFields={props.versionedFields}
      versionedTags={props.versionedTags}
      connectionSchemaId={props.connectionSchemaId}
      versionedEquipmentId={props.versionedEquipmentId}
      versionedConnectionId={props.versionedConnectionId}
      connectionStatus={props.connectionStatus}
      shouldRecordEquipment={props.shouldRecordEquipment}
      viewer={props.viewer}
    />
  );
}

AssayRunFields.defaultProps = {
  isEditable: false,
};

export default AssayRunFields;
